import type { StatementPeriodRequest } from '../api/reviews';
import { monthOf } from './reviewMonths';

/**
 * Statement period helpers (spec §10.13.4). The SERVER decides what is sent (`StatementPeriodRules`); these only
 * mirror its default for display and build the request. The statement period is not the review period.
 */
export interface StatementChoice {
  custom: boolean;
  /** `YYYY-MM`. */
  start: string;
  end: string;
}

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Year-to-date of the year the review ends in: Jan → the review's end month. */
export function yearToDate(reviewPeriodEnd: string): StatementChoice {
  const end = monthOf(reviewPeriodEnd) ?? '';
  return { custom: false, start: end ? `${end.slice(0, 4)}-01` : '', end };
}

/** A custom choice is usable only with two real months, the first not after the last. YTD is always usable. */
export function isUsable(choice: StatementChoice): boolean {
  if (!choice.custom) return true;
  return MONTH.test(choice.start) && MONTH.test(choice.end) && choice.start <= choice.end;
}

/** The request fields: months only when custom, so an unticked box can never send a stale range. */
export function toRequest(choice: StatementChoice): StatementPeriodRequest {
  return choice.custom
    ? { statementCustom: true, statementStart: choice.start, statementEnd: choice.end }
    : { statementCustom: false };
}

/** Identity of a choice, to tell whether the draft was written for the choice now on screen. */
export function choiceKey(choice: StatementChoice): string {
  return choice.custom ? `custom:${choice.start}:${choice.end}` : 'ytd';
}
